import { NumberFormatter } from '@repo/toolkit/number'
import { BackIcon } from '@repo/ui/icons'
import Modal from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { getPaymentRecordsStatusesStatistics } from '../../../../api/enrollments/get-payment-records-statistics'
import { useObserver } from '../../../../lib/contexts/observer'
import { paymentTransactionStatusEnum } from '../../../../lib/types/enums/payments/payment-transaction-status.enum'
import RefundEnrollmentForm, { REFUND_FAILED_MESSAGE } from './refund-enrollment-form'

type Props = {
  enrollmentId: number
}

/**
 * A price drop never refunds the family on its own: when a modification leaves the enrollment
 * overpaid, LEC is asked whether to refund the overpayment, and can say no.
 */
const EnrollmentOverpaymentRefundModal: React.FC<Props> = ({ enrollmentId }) => {
  const [overpaidAmountToRefund, setOverpaidAmountToRefund] = useState<number | null>(null)
  // the overpayment before the last modification, so only a new overpayment asks the question
  const previousOverpaidAmount = useRef<number | null>(null)

  const { data: statistics, refetch } = useQuery({
    queryKey: ['paymentRecordsStatistics', enrollmentId],
    queryFn: () => getPaymentRecordsStatusesStatistics({ enrollmentId })
  })

  // an overpayment that already exists when the page opens is shown in the payment summary only
  useEffect(() => {
    if (statistics && previousOverpaidAmount.current === null)
      previousOverpaidAmount.current = statistics.overpaidAmount
  }, [statistics])

  const { notify } = useObserver({
    key: 'enrollment-overpayment',
    update: async () => {
      const previous = previousOverpaidAmount.current ?? 0
      const { data } = await refetch()
      if (!data) return

      previousOverpaidAmount.current = data.overpaidAmount
      if (data.overpaidAmount > previous) setOverpaidAmountToRefund(data.overpaidAmount)
    }
  })

  const close = () => setOverpaidAmountToRefund(null)

  return (
    <Modal
      isOpen={overpaidAmountToRefund !== null}
      title="Rembourser le trop-perçu ?"
      hideActions
      icon={BackIcon}
      onCancel={close}
    >
      {overpaidAmountToRefund !== null && (
        <>
          <Typography variant="secondary-body" className="px-4 pt-4">
            {`Le nouveau prix est inférieur au montant déjà payé. Trop-perçu : ${NumberFormatter.formatCurrency(
              overpaidAmountToRefund
            )}. Souhaitez-vous rembourser ?`}
          </Typography>
          <RefundEnrollmentForm
            enrollmentId={enrollmentId}
            defaultAmount={overpaidAmountToRefund}
            confirmText="Oui"
            cancelText="Non"
            onCancel={close}
            onSuccess={({ result }) => {
              close()
              notify([
                'enrollment-overpayment',
                'enrollment-profile-monitoring-payment-record',
                'enrollment-payment-record'
              ])
              if (result !== paymentTransactionStatusEnum.SUCCEEDED)
                toast.error(REFUND_FAILED_MESSAGE)
            }}
          />
        </>
      )}
    </Modal>
  )
}

export default EnrollmentOverpaymentRefundModal
